"use client";

import { useMemo, useState } from "react";
import type { PostCard as V1PostCard } from "@theta/api-client";
import { markPostsRead } from "@/lib/api/posts";

/**
 * ============================================================================
 * WHAT "SET" MEANS, AND WHEN THE SERVER GETS TOLD — OSO-622
 * ============================================================================
 *
 * Two module-scope pieces, one hook:
 *
 *  - A LEDGER + DEBOUNCED QUEUE (`markRead`): dwelling on a card reports the
 *    newest content instant that card RENDERED. Marks are batched (~2 s) into
 *    one `POST /posts/read`, because a scroll pass dwells on several cards and
 *    each deserving its own request would be a request storm shaped exactly
 *    like the one `lib/threadFocus.ts` exists to prevent.
 *  - A PER-MOUNT SNAPSHOT (`useUnreadSnapshot`): what the card HIGHLIGHTS is
 *    frozen once per pageview — on the first render whose payload came from a
 *    post-mount fetch, not off a warm remount's stale cache — so the very
 *    mark the ledger writes — or a mark advanced from another tab — can never
 *    un-highlight anything mid-view.
 *
 * A module-level store rather than context, for the same reason
 * `lib/threadFocus.ts` is one: it is per tab, every reader is a leaf, and the
 * ledger has to survive a card unmounting (virtualized away, navigated past)
 * with its report already made.
 */

/** postId -> newest epoch ms already reported (or queued). Set BEFORE the
 *  request goes out, so a failed flush is not retried in a loop — one honest
 *  miss per pageview is fine at this scale, and the next pageview re-reports. */
const reported = new Map<number, number>();

/** postId -> the ISO `seenAt` still waiting for the next flush. */
const queue = new Map<number, string>();

/** ~2 s: long enough to collect a scroll pass's dwell marks into one batch,
 *  short enough that closing the tab rarely loses more than a pageview's
 *  worth — which the ledger's failure story already tolerates. */
const FLUSH_MS = 2000;

/** The route caps a batch at 50 marks (422 above it); a flush larger than
 *  that — twenty-card pages make it near impossible, but nothing enforces
 *  impossible — goes out as several. */
const MAX_BATCH = 50;

let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  flushTimer = null;
  if (queue.size === 0) return;
  // Ids SORTED (deadlock hygiene): two tabs flushing concurrently upsert the
  // same rows in the same order instead of crossing lock acquisition.
  const ids = [...queue.keys()].sort((a, b) => a - b);
  for (let i = 0; i < ids.length; i += MAX_BATCH) {
    const marks = ids
      .slice(i, i + MAX_BATCH)
      .map((id) => ({ id, seenAt: queue.get(id)! }));
    // Fire-and-forget, and deliberately NO query invalidation on success: the
    // highlights on screen are frozen per mount, and the feed fingerprints
    // deliberately do not watch the mark (backend A4) — so there is nothing a
    // re-read could change except burning the poll budget.
    void markPostsRead(marks).catch(() => {});
  }
  queue.clear();
}

/** What the stamp fold needs of a rendered comment row — structural, so both
 *  the payload's `PostComment` and the thread query's `Comment` satisfy it
 *  without this lib module importing from `components/`. */
interface StampComment {
  createdAt: string;
  publishedAt?: string | null;
}

/**
 * The newest content instant this card RENDERED — what `seenAt` echoes back
 * to the server. Per comment it is `publishedAt ?? createdAt`, the SAME
 * coalesce the server counts unread by: an OSO-490 held-back comment becomes
 * visible at `publishedAt`, and reporting its earlier `createdAt` would leave
 * the server counting a comment you have read as unread forever.
 *
 * `rows` is the separately-fetched thread, for the detail page: its payload
 * never embeds comments (`SinglePostClient` hands the card `comments: []`),
 * so folding only `post.comments` there would stamp the post's own creation
 * instant and reading the thread would never advance the mark past it.
 */
export function newestStamp(
  post: Pick<V1PostCard, "createdAt" | "comments">,
  rows: readonly StampComment[] = [],
): string {
  let newest = post.createdAt;
  for (const c of [...post.comments, ...rows]) {
    const instant = c.publishedAt ?? c.createdAt;
    if (instant > newest) newest = instant;
  }
  // ISO-8601 with a fixed layout compares correctly as a string, and keeping
  // the server's own string means no client-side re-rendering of the instant.
  return newest;
}

/**
 * Report "this viewer has seen this post's thread through `seenAt`".
 *
 * Idempotent and forward-only from the caller's side too: a dwell that fires
 * again (scroll away and back), or a detail page re-rendering every poll
 * tick, hits the ledger and costs nothing. `enabled` gates on being logged
 * in — a logged-out mark would 401 once per card, which is Sentry noise, and
 * the server has no row to advance for it anyway.
 */
export function markRead(postId: number, seenAt: string, enabled: boolean): void {
  if (!enabled) return;
  const stamp = Date.parse(seenAt);
  if (Number.isNaN(stamp)) return;
  const prev = reported.get(postId);
  if (prev !== undefined && prev >= stamp) return;
  reported.set(postId, stamp);
  queue.set(postId, seenAt);
  flushTimer ??= setTimeout(flush, FLUSH_MS);
}

/** What `useUnreadSnapshot` needs of a comment row — structural, so both the
 *  thread's `Comment` and the feed preview's `PostComment` satisfy it without
 *  this lib module importing from `components/`. */
interface SnapshotComment {
  id: number;
  createdAt: string;
  author: { id: number } | null;
}

const EMPTY_IDS: ReadonlySet<number> = new Set();

/**
 * The sticky pageview snapshot: which comments are new-to-you, decided ONCE.
 *
 * FROZEN ON THE FIRST RENDER WHERE `baselineReady` IS TRUE, then never again
 * — that latch is the whole sticky requirement. Later poll refreshes that
 * republish the card with an advanced `viewerLastSeenAt` (the mark moved
 * because this viewer dwelled, commented, or read the post in another tab)
 * can never UN-highlight anything. The snapshot is PER MOUNT: navigating
 * away and back starts a new pageview with a fresh baseline — that is the
 * design, not a leak.
 *
 * WHY THE BASELINE WAITS FOR `baselineReady` instead of freezing at mount:
 * on a warm remount within react-query's gcTime the cached pages render
 * instantly, carrying a `viewerLastSeenAt` from BEFORE the marks the last
 * visit wrote (the mutation deliberately invalidates nothing) — freezing
 * that would re-highlight comments the viewer just read, for the whole
 * pageview. Same reasoning as `useFrozenFeedOrder` refusing to freeze the
 * cached order: the home feed passes `isFetchedAfterMount`, so the card
 * renders QUIET until the first post-mount fetch lands and freezes what that
 * fetch delivered. Quiet-until-ready never un-highlights (nothing was lit),
 * so stickiness survives. Surfaces that never highlight (group feeds, the
 * detail page) default the flag to true and are unaffected.
 *
 * The derivation is grow-only across polls: a foreign comment arriving
 * mid-view has `createdAt > frozen.since` and lights up; the viewer's OWN
 * comment is excluded by the author check (own activity is never new-to-you —
 * the server counts with the same `author_id != viewer` predicate, so no
 * client write hook is needed and `useCreateComment` stays untouched).
 *
 * Anonymous rows arrive `author: null` to EVERYONE — including their own
 * author (serialize.ts withholds the object from the wire; the optimistic
 * row in `useCreateComment` is `author: null` too) — so the author check
 * above CANNOT catch the viewer's own anonymous comment. The server never
 * counts it (its predicate runs on the DB's `author_id` column, and the
 * own-comment route hook advances the mark past it), so the only effect is
 * a possible brief self-highlight of that one comment within the same
 * pageview, from a concurrently mounted feed card in another tab. Accepted.
 *
 * Accepted edge (arbitration A1): the client compares bare `createdAt` while
 * the server counts by `coalesce(publishedAt, createdAt)` — equal for every
 * instantly-published comment, which in prod is all of them. A
 * transcode-delayed comment (OSO-490) can therefore be in the server's count
 * yet carry a `createdAt` older than the mark and never highlight; the header
 * count stays honest either way (`max` below). Accepted, per the arbitration
 * — stated here so nobody "fixes" the predicate and diverges the two sides
 * in some subtler way.
 */
export function useUnreadSnapshot(
  post: Pick<V1PostCard, "viewerLastSeenAt" | "unreadCommentCount">,
  currentUserId: number | null,
  comments: readonly SnapshotComment[],
  baselineReady = true,
): { unreadIds: ReadonlySet<number>; unreadCount: number } {
  const [frozen, setFrozen] = useState(() =>
    baselineReady
      ? { since: post.viewerLastSeenAt, count: post.unreadCommentCount }
      : null,
  );
  // Latch-once, adjusted DURING render (React's escape hatch for deriving
  // state from a prop — `useThreadFocus` does the same), so the freeze lands
  // in the same commit as the fetch that made the values trustworthy.
  if (frozen === null && baselineReady) {
    setFrozen({ since: post.viewerLastSeenAt, count: post.unreadCommentCount });
  }
  const since = frozen?.since ?? null;
  const unreadIds = useMemo(() => {
    // No baseline yet (warm remount, first fetch still in flight) or no mark
    // (or logged out — the server sends null there) = everything seen: the
    // quiet card, per the design.
    if (since == null) return EMPTY_IDS;
    const sinceMs = Date.parse(since);
    const ids = new Set<number>();
    for (const c of comments) {
      if (Date.parse(c.createdAt) > sinceMs && c.author?.id !== currentUserId) {
        ids.add(c.id);
      }
    }
    return ids as ReadonlySet<number>;
  }, [comments, currentUserId, since]);
  /**
   * The wire's count is the WHOLE thread's and the visible list is a bounded
   * preview, so neither alone is right — the larger is. The same
   * larger-of-two-honest-numbers pattern as `replyCount` in `PostCard`; and
   * because both arms only grow within a mount, the header count never
   * shrinks mid-view.
   */
  const unreadCount = frozen === null ? 0 : Math.max(frozen.count, unreadIds.size);
  return { unreadIds, unreadCount };
}

/**
 * Test seam — the ledger and queue are module state, so tests reset them
 * between cases the way `__resetThreadFocus` resets the live thread.
 */
export function __resetPostReads(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  reported.clear();
  queue.clear();
}
